import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpenseExternalPartySelect } from '@/components/shared/expense-external-party-select';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { FormField } from '@/components/shared/form-field';
import { FundSelect } from '@/components/shared/fund-select';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Listbox } from '@/components/ui/listbox';
import { MoneyInput } from '@/components/ui/money-input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { CONTRIBUTION_METHODS } from '@/lib/contribution-methods';
import { churchToday, formatMoney, formatPercent } from '@/utils/format';

interface FundLine {
  key: string;
  fundId: string | null;
  amountMinor: number | null;
}

const newFundLine = (): FundLine => ({
  key: crypto.randomUUID(),
  fundId: null,
  amountMinor: null,
});

type Direction = 'in' | 'out';
type PartyKind = 'member' | 'company' | 'service' | 'other';

const DIRECTIONS: Direction[] = ['in', 'out'];
const PARTY_KINDS: PartyKind[] = ['member', 'company', 'service', 'other'];

interface Gathering {
  id: string;
  name: string;
  kind: 'service' | 'program';
}

interface Member {
  id: string;
  firstName: string;
  lastName: string;
}

export interface FinancialPositionRecordTransactionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: string;
  itemName: string;
  currency: string;
}

export const FinancialPositionRecordTransactionDialog = ({
  open,
  onOpenChange,
  itemId,
  itemName,
  currency,
}: FinancialPositionRecordTransactionDialogProps) => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [direction, setDirection] = useState<Direction>('in');
  const [method, setMethod] = useState<(typeof CONTRIBUTION_METHODS)[number]>('cash');
  const [partyKind, setPartyKind] = useState<PartyKind>('member');
  const [memberId, setMemberId] = useState<string | null>(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [externalPartyId, setExternalPartyId] = useState<string | null>(null);
  const [programId, setProgramId] = useState<string | null>(null);
  const [partyName, setPartyName] = useState('');
  const [transactionDate, setTransactionDate] = useState(churchToday());
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [fundLines, setFundLines] = useState<FundLine[]>(() => [newFundLine()]);
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDirection('in');
      setMethod('cash');
      setPartyKind('member');
      setMemberId(null);
      setMemberQuery('');
      setExternalPartyId(null);
      setProgramId(null);
      setPartyName('');
      setTransactionDate(churchToday());
      setReference('');
      setNote('');
      setFundLines([newFundLine()]);
    }
  }

  const members = useQuery({
    queryKey: ['people', 'members', 'picker', 'account-transaction', memberQuery],
    queryFn: () =>
      api.get<{ data: Member[] }>(
        `/people/members?pageSize=20${memberQuery ? `&q=${encodeURIComponent(memberQuery)}` : ''}`,
      ),
    enabled: open && partyKind === 'member',
    placeholderData: keepPreviousData,
  });

  const memberOptions = useMemo(
    () =>
      (members.data?.data ?? []).map((member) => ({
        id: member.id,
        label: `${member.firstName} ${member.lastName}`.trim(),
      })),
    [members.data?.data],
  );
  const selectedMember = memberOptions.find((option) => option.id === memberId) ?? null;

  const gatherings = useQuery({
    queryKey: ['programs', 'account-transaction'],
    queryFn: () => api.get<{ data: Gathering[] }>('/programs?pageSize=100'),
    enabled: open && direction === 'in' && partyKind === 'service',
  });

  const gatheringOptions = useMemo(
    () =>
      [...(gatherings.data?.data ?? [])]
        .sort((left, right) => left.name.localeCompare(right.name))
        .map((row) => ({
          value: row.id,
          label: row.kind === 'program' ? `${row.name} (${t('financialPosition.transactions.programKind')})` : row.name,
        })),
    [gatherings.data?.data, t],
  );

  const partyOptions = (direction === 'in' ? PARTY_KINDS : PARTY_KINDS.filter((kind) => kind !== 'service')).map(
    (value) => ({
      value,
      label: t(`financialPosition.transactions.partyKinds.${value}`),
    }),
  );

  const partyReady =
    partyKind === 'member'
      ? Boolean(memberId)
      : partyKind === 'company'
        ? Boolean(externalPartyId)
        : partyKind === 'service'
          ? Boolean(programId)
          : partyName.trim().length > 0;

  const assignedMinor = fundLines.reduce((sum, line) => sum + (line.amountMinor ?? 0), 0);
  const fundIds = fundLines.map((line) => line.fundId).filter((id): id is string => Boolean(id));
  const fundSplitReady =
    fundLines.length > 0 &&
    assignedMinor > 0 &&
    fundLines.every((line) => line.amountMinor !== null && line.amountMinor > 0) &&
    new Set(fundIds).size === fundIds.length;

  const canSave = Boolean(transactionDate && partyReady && fundSplitReady);

  const save = useMutation({
    mutationFn: () =>
      api.post(`/financial-position/items/${itemId}/transactions`, {
        direction,
        method,
        partyKind,
        memberId: partyKind === 'member' ? memberId : undefined,
        externalPartyId: partyKind === 'company' ? externalPartyId : undefined,
        programId: partyKind === 'service' ? programId : undefined,
        partyName: partyKind === 'other' ? partyName.trim() : undefined,
        amountMinor: assignedMinor,
        transactionDate,
        reference: reference.trim() || null,
        note: note.trim() || null,
        funds: fundLines.map((line) => ({
          fundId: line.fundId ?? undefined,
          amountMinor: line.amountMinor,
        })),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['financial-position'] });
      await queryClient.invalidateQueries({ queryKey: ['reports', 'run', 'balance_sheet'] });
      toast.success(t('financialPosition.transactions.saved'));
      onOpenChange(false);
    },
    onError: (error) => {
      if (error instanceof ApiError) toast.error(tErrors(error.code, error.params));
      else toast.error(tCommon('toast.somethingWentWrong'));
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" closeLabel={tCommon('action.close')} className="overflow-visible">
        <DialogHeader>
          <DialogTitle>{t('financialPosition.transactions.record')}</DialogTitle>
        </DialogHeader>
        <DialogBody
          className="overflow-visible"
          contentClassName="flex flex-col gap-6"
        >
          <p className="text-body-sm text-fg-secondary">
            {t('financialPosition.transactions.description', { name: itemName })}
          </p>
          <div
            className="grid items-start gap-6 md:grid-cols-[minmax(0,35%)_minmax(0,65%)]"
          >
          <div className="flex min-w-0 flex-col gap-4">
          <FormField label={t('financialPosition.transactions.direction')} required>
            {(field) => (
              <Listbox
                id={field.id}
                value={direction}
                onChange={(next) => {
                  const value = (next as Direction) ?? 'in';
                  setDirection(value);
                  if (value === 'out' && partyKind === 'service') {
                    setPartyKind('member');
                    setProgramId(null);
                  }
                }}
                options={DIRECTIONS.map((value) => ({
                  value,
                  label: t(`financialPosition.transactions.directions.${value}`),
                }))}
                popoverPortal="dialog"
              />
            )}
          </FormField>
          <FormField label={t('financialPosition.transactions.method')} required>
            {(field) => (
              <Listbox
                id={field.id}
                value={method}
                onChange={(next) => {
                  setMethod((next as (typeof CONTRIBUTION_METHODS)[number]) ?? 'cash');
                }}
                options={CONTRIBUTION_METHODS.map((value) => ({
                  value,
                  label: t(`expenses.methods.${value}`),
                }))}
                popoverPortal="dialog"
              />
            )}
          </FormField>
          <FormField label={t('financialPosition.transactions.party')} required>
            {(field) => (
              <Listbox
                id={field.id}
                value={partyKind}
                onChange={(next) => {
                  const kind = (next as PartyKind) ?? 'member';
                  setPartyKind(kind);
                  setMemberId(null);
                  setExternalPartyId(null);
                  setProgramId(null);
                  setPartyName('');
                }}
                options={partyOptions}
                popoverPortal="dialog"
              />
            )}
          </FormField>
          {partyKind === 'member' ? (
            <FormField label={t('financialPosition.transactions.member')} required>
              {() => (
                <EntityCombobox
                  value={memberId}
                  onChange={setMemberId}
                  options={memberOptions}
                  selected={selectedMember}
                  onQueryChange={setMemberQuery}
                  loading={members.isPending && !members.data}
                  popoverPortal="dialog"
                  clearLabel={tCommon('action.clearSelection')}
                  placeholder={t('financialPosition.transactions.memberPlaceholder')}
                />
              )}
            </FormField>
          ) : null}
          {partyKind === 'company' ? (
            <FormField label={t('financialPosition.transactions.company')} required>
              {(field) => (
                <ExpenseExternalPartySelect
                  id={field.id}
                  value={externalPartyId}
                  onChange={(partyId) => setExternalPartyId(partyId)}
                  popoverPortal="dialog"
                />
              )}
            </FormField>
          ) : null}
          {partyKind === 'service' ? (
            <FormField label={t('financialPosition.transactions.service')} required>
              {(field) => (
                <Listbox
                  id={field.id}
                  value={programId}
                  onChange={setProgramId}
                  options={gatheringOptions}
                  placeholder={t('financialPosition.transactions.servicePlaceholder')}
                  popoverPortal="dialog"
                  disabled={gatherings.isLoading || gatheringOptions.length === 0}
                />
              )}
            </FormField>
          ) : null}
          {partyKind === 'other' ? (
            <FormField
              label={
                direction === 'in'
                  ? t('financialPosition.transactions.otherNameIn')
                  : t('financialPosition.transactions.otherName')
              }
              required
            >
              {(field) => (
                <Input
                  id={field.id}
                  value={partyName}
                  onChange={(event) => setPartyName(event.target.value)}
                  placeholder={
                    direction === 'in'
                      ? t('financialPosition.transactions.otherNameInPlaceholder')
                      : t('financialPosition.transactions.otherNamePlaceholder')
                  }
                />
              )}
            </FormField>
          ) : null}
          <FormField label={t('financialPosition.transactions.date')} required>
            {(field) => (
              <DatePicker
                id={field.id}
                popoverPortal="dialog"
                value={transactionDate}
                onChange={(value) => setTransactionDate(value ?? churchToday())}
              />
            )}
          </FormField>
          </div>
          <div className="flex min-w-0 flex-col gap-4">
            <div className="flex min-w-0 flex-col gap-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-body-sm font-medium text-fg">
                    {t('financialPosition.transactions.fundSplit.title')}
                  </p>
                  <p className="text-caption text-fg-secondary">
                    {t('financialPosition.transactions.fundSplit.hint')}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setFundLines((current) => [...current, newFundLine()])}
                >
                  <Plus className="size-4" />
                  {t('financialPosition.transactions.fundSplit.add')}
                </Button>
              </div>
              {fundLines.length > 0 ? (
                <div className="min-w-0 rounded-lg border border-border bg-surface">
                  <table className="w-full table-fixed border-collapse text-body-sm">
                    <colgroup>
                      <col className="w-[46%]" />
                      <col className="w-[34%]" />
                      <col className="w-[12%]" />
                      <col className="w-[8%]" />
                    </colgroup>
                    <thead className="bg-surface-sunken">
                      <tr>
                        <th scope="col" className="px-3 py-2.5 text-left font-medium text-fg-secondary">
                          {t('financialPosition.transactions.fundSplit.fund')}
                        </th>
                        <th scope="col" className="px-3 py-2.5 text-right font-medium text-fg-secondary">
                          {t('financialPosition.transactions.fundSplit.amount')}
                        </th>
                        <th scope="col" className="px-3 py-2.5 text-right font-medium text-fg-secondary">
                          {t('financialPosition.transactions.fundSplit.share')}
                        </th>
                        <th scope="col" className="px-2 py-2.5">
                          <span className="sr-only">{t('financialPosition.transactions.fundSplit.remove')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {fundLines.map((line) => (
                        <tr key={line.key} className="border-t border-border">
                          <td className="min-w-0 px-3 py-2.5">
                            <FundSelect
                              className="min-w-0 max-w-full"
                              value={line.fundId}
                              allOptionLabel={t('financialPosition.transactions.fundSplit.notTied')}
                              allowCreate={false}
                              onChange={(fundId) =>
                                setFundLines((current) =>
                                  current.map((item) =>
                                    item.key === line.key ? { ...item, fundId } : item,
                                  ),
                                )
                              }
                              popoverPortal="dialog"
                              aria-label={t('financialPosition.transactions.fundSplit.fund')}
                            />
                          </td>
                          <td className="min-w-0 px-3 py-2.5">
                            <MoneyInput
                              className="min-w-0 max-w-full"
                              value={line.amountMinor}
                              currency={currency}
                              onChange={(amount) =>
                                setFundLines((current) =>
                                  current.map((item) =>
                                    item.key === line.key ? { ...item, amountMinor: amount } : item,
                                  ),
                                )
                              }
                              aria-label={t('financialPosition.transactions.fundSplit.amount')}
                            />
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-fg-secondary">
                            {assignedMinor > 0 && line.amountMinor
                              ? formatPercent(line.amountMinor / assignedMinor)
                              : '—'}
                          </td>
                          <td className="px-1 py-2.5 text-right">
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              aria-label={t('financialPosition.transactions.fundSplit.remove')}
                              onClick={() =>
                                setFundLines((current) =>
                                  current.filter((item) => item.key !== line.key),
                                )
                              }
                            >
                              <span aria-hidden>×</span>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t-2 border-border bg-surface-sunken font-medium">
                      <tr>
                        <td className="px-3 py-2.5 text-fg">
                          {t('financialPosition.transactions.fundSplit.total')}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-fg">
                          {formatMoney(assignedMinor, currency)}
                        </td>
                        <td />
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : null}
            </div>
              <FormField label={t('financialPosition.transactions.reference')}>
                {(field) => (
                  <Input
                    id={field.id}
                    value={reference}
                    onChange={(event) => setReference(event.target.value)}
                    placeholder={t('financialPosition.transactions.referencePlaceholder')}
                    maxLength={120}
                  />
                )}
              </FormField>
              <FormField label={t('financialPosition.transactions.note')}>
                {(field) => (
                  <Textarea
                    id={field.id}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    rows={3}
                    maxLength={2000}
                    placeholder={t('financialPosition.transactions.notePlaceholder')}
                  />
                )}
              </FormField>
          </div>
          </div>
        </DialogBody>
        <DialogFooter className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {tCommon('action.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!canSave}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {t('financialPosition.transactions.record')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
